import { toast } from "sonner";

/**
 * Show a toast with an undo action. If the user clicks "Undo" within the
 * timeout window, `onUndo` is called. Otherwise, `onExpire` is called
 * after the toast dismisses.
 *
 * @example
 * showUndoToast({
 *   message: "Contribution marked as reviewed",
 *   onUndo: () => revertStatus(contributionId),
 *   onExpire: () => persistStatus(contributionId),
 * });
 */

interface UndoToastOptions {
  /** Main message text, e.g. "Contribution marked as reviewed" */
  message: string;
  /** Called if the user clicks the Undo action */
  onUndo: () => void | Promise<void>;
  /** Called when the toast dismisses without undo (optional) */
  onExpire?: () => void;
  /** Duration in ms before auto-dismiss. Default 8000 (8 seconds). */
  duration?: number;
  /** Description text below the main message (optional) */
  description?: string;
}

export function showUndoToast({
  message,
  onUndo,
  onExpire,
  duration = 8000,
  description,
}: UndoToastOptions) {
  let undoClicked = false;

  toast(message, {
    description,
    duration,
    action: {
      label: "Undo",
      onClick: () => {
        undoClicked = true;
        onUndo();
      },
    },
    onDismiss: () => {
      if (!undoClicked && onExpire) {
        onExpire();
      }
    },
  });
}

/**
 * Standard success toast (no undo). Follows the plan's toast pattern:
 * green left border, auto-dismiss in 5 seconds.
 */
export function showSuccessToast(message: string, description?: string) {
  toast.success(message, { description, duration: 5000 });
}

/**
 * Warning toast. Amber, persists until dismissed.
 */
export function showWarningToast(message: string, description?: string) {
  toast.warning(message, { description, duration: Infinity });
}

/**
 * Error toast. Red, persists until dismissed.
 */
export function showErrorToast(message: string, description?: string) {
  toast.error(message, { description, duration: Infinity });
}

/**
 * Info toast. Blue, auto-dismiss in 5 seconds.
 */
export function showInfoToast(message: string, description?: string) {
  toast.info(message, { description, duration: 5000 });
}
